import { Divider, Stack } from "@tm/components"
import { useWorkTask, WorkTaskInfo } from "@tm/context-distribution"
import { notUndefinedOrNull } from "@tm/utils"
import { useCallback, useMemo, useRef, useState } from "react"
import {
    AddCustomRepairTimeItemsRequest,
    CustomRepairTimeItemResponse,
    EditCustomRepairTimeItemRequest,
    useAddCustomRepairTimeItems,
    useCustomRepairTimesDefaults,
    useDeleteCustomRepairTimes,
    useEditCustomRepairTimeItem,
    useShowCustomRepairTimeItems,
} from "@tm/data/basket"
import { mapCustomRepairTimeItemToCustomWork } from "@bundles/compilations/data/mapper/customItems"
import { useWorkTaskBasketState } from "@bundles/basket"
import { useLocalization } from "@tm/localization"
import { ModuleHeader } from "./ModuleHeader"
import { useOwnRepairTimesStore } from "./store"
import { RepairTimeListHandle, RepairTimesList } from "./RepairTimesList"
import { DialogCustomRepairTime, EditDialog } from "./EditDialog"

type OwnRepairTimesRouteParams = {
    listId?: string
}

type OwnRepairTimesProps = OwnRepairTimesRouteParams & {
    onClose?(): void
}

export function OwnRepairTimes({ onClose }: OwnRepairTimesProps) {
    const { workTask } = useWorkTask() ?? {}

    if (!workTask) {
        return null
    }
    return <OwnRepairTimesComponent onClose={onClose} workTask={workTask} />
}

function OwnRepairTimesComponent({ workTask, onClose }: OwnRepairTimesProps & { workTask: WorkTaskInfo }) {
    const { searchQuery, repairTimeToEdit, setRepairTimeToEdit, setIsSaving } = useOwnRepairTimesStore()
    const { translateText } = useLocalization()
    const [saveError, setSaveError] = useState<string>()

    const {
        costEstimation: {
            state: { costEstimation: { costEstimationOptions } = {} },
            actions: { addCustomWorks },
        },
    } = useWorkTaskBasketState(workTask.id)
    const { customRepairTimesDefaults: { defaultRepairTimeDivision } = {} } = useCustomRepairTimesDefaults()

    const { mutateAsync: saveEditedRepairTime } = useEditCustomRepairTimeItem()
    const { mutateAsync: saveNewRepairTime } = useAddCustomRepairTimeItems()
    const { mutateAsync: deleteRepairTimes } = useDeleteCustomRepairTimes()

    const listHandle = useRef<RepairTimeListHandle>(null)

    const listRequest = useMemo<Parameters<typeof useShowCustomRepairTimeItems>[0]>(
        () => ({
            query: searchQuery,
        }),
        [searchQuery]
    )

    const { data: { pages } = {}, isFetchingNextPage, isLoading, fetchNextPage } = useShowCustomRepairTimeItems(listRequest)
    const repairTimes = useMemo(() => (pages?.flatMap((x) => x.data) ?? []).filter(notUndefinedOrNull), [pages])

    const handleDeleteRepairTimes = useCallback(
        async (repairTimesResponse: CustomRepairTimeItemResponse[]) => {
            await deleteRepairTimes(repairTimesResponse.map((x) => x.id))
        },
        [deleteRepairTimes]
    )

    const handleCloseDialog = useCallback(() => {
        setRepairTimeToEdit(undefined)
        setSaveError(undefined)
    }, [])

    const handleAddToCostEstimation = useCallback(
        (repairTimes: CustomRepairTimeItemResponse[]) =>
            addCustomWorks({
                workTaskId: workTask.id,
                repairTimeDivision: defaultRepairTimeDivision,
                customWorks: repairTimes.map(mapCustomRepairTimeItemToCustomWork),
                vehicleId: workTask.vehicle?.id,
                usePercentageValues: true,
                costEstimationOptions,
            }),
        [addCustomWorks, workTask.id, workTask.vehicle?.id, defaultRepairTimeDivision, costEstimationOptions]
    )

    const handleEditRepairTime = useCallback((repairTime: CustomRepairTimeItemResponse) => {
        const selectedVatRate = repairTime.vatRates.find((vatRate) => vatRate.isSelected) ?? repairTime.vatRates[0]
        const { id, description, version, workId, category, customHourlyRate, fixedPriceValue, workTime } = repairTime
        setRepairTimeToEdit({
            id,
            description,
            version,
            workId,
            category,
            customHourlyRate,
            fixedPriceValue,
            workTime,
            selectedVatRate,
            type: "edit",
        })
    }, [])

    const handleSaveRepairTime = useCallback(
        async (repairTime: DialogCustomRepairTime) => {
            try {
                setIsSaving(true)
                setSaveError(undefined)
                if (repairTime.type === "edit") {
                    const request: EditCustomRepairTimeItemRequest = {
                        customRepairTimeToEdit: repairTime,
                        repairTimeDivision: 1, // TODO
                    }
                    await saveEditedRepairTime(request)
                } else if (repairTime.description) {
                    const { type, ...customRepairTimeItemToAdd } = repairTime
                    const request: AddCustomRepairTimeItemsRequest = {
                        customRepairTimeItemsToAdd: [customRepairTimeItemToAdd],
                        repairTimeDivision: 1, // TODO
                    }
                    await saveNewRepairTime(request)
                    listHandle.current?.scrollToTop()
                }
                handleCloseDialog()
            } catch {
                setSaveError(translateText(13452))
            } finally {
                setIsSaving(false)
            }
        },
        [handleCloseDialog, saveEditedRepairTime, saveNewRepairTime]
    )

    return (
        <Stack gap={2} flex={1} position="relative">
            <ModuleHeader onClose={onClose} />
            <Stack direction="row" gap={2} px={2} divider={<Divider orientation="vertical" />} minHeight={0} height="100%">
                <RepairTimesList
                    repairTimes={repairTimes}
                    pageIndex={pages?.length ?? 0}
                    isLoading={isLoading || isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    onAddRepairTimesToCostEstimation={handleAddToCostEstimation}
                    onDeleteRepairTimes={handleDeleteRepairTimes}
                    onEditRepairTime={handleEditRepairTime}
                />
            </Stack>
            {repairTimeToEdit && (
                <EditDialog onClose={handleCloseDialog} onSave={handleSaveRepairTime} repairTime={repairTimeToEdit} saveError={saveError} />
            )}
        </Stack>
    )
}
